import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardSection,
  Flex,
  LabelIcon,
  Link,
  Select,
  Text,
  SkeletonShape,
} from '@customerio/pluma-components/react';
import { Chart } from '@/components/highcharts';
import { generatePath } from '@/services/router/generate-path';
import { useServicesQuery } from '@/services/api';
import { useAnalytics } from '@/services/analytics';
import { SectionLabel } from './section-label';
import * as styles from './activity-chart.css';
import {
  aggregateMetrics,
  normalizeTimezone,
  ALL_CHANNELS_OPTION,
  CHANNEL_OPTIONS,
  channelSupportsDelivered,
  channelSupportsOpens,
  getEnabledChannelOptions,
  type ChannelOptionValue,
} from './activity-chart-utils';

function analysisHref(workspaceId: number): string {
  const now = new Date();
  const start = new Date(now.getTime() - 30 * 86400 * 1000);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const params = new URLSearchParams({
    channels: 'email_twilio_webhook_slack_in_app',
    data_types: 'campaign_newsletter_broadcast',
    start_time: fmt(start),
    end_time: fmt(now),
    measures:
      'delivery_metrics.sent-delivery_metrics.delivered-delivery_metrics.opened-delivery_metrics.converted-delivery_metrics.unsubscribed',
  });
  return generatePath('/workspaces/:workspaceId/journeys/analysis', { workspaceId }) + '?' + params.toString();
}

// NOTE: Do not call loadHighcharts() at module scope. The Chart component
// handles async loading internally and shows a placeholder until ready.

interface ActivityChartProps {
  workspaceId: number;
  isStale?: boolean;
}

function useWorkspaceTimezone(workspaceId: number) {
  const { data, isSuccess } = useServicesQuery(
    'getWorkspaceSettings',
    { params: { environment_id: workspaceId } },
    { staleTime: 300_000, enabled: workspaceId > 0 },
  );
  const tz = data?.settings?.metrics_timezone;
  const resolved =
    tz != null && tz.length > 0 ? normalizeTimezone(tz) : Intl.DateTimeFormat().resolvedOptions().timeZone;
  return { tz: resolved, isReady: isSuccess };
}

function useDeliveryMetrics(workspaceId: number) {
  const { tz, isReady } = useWorkspaceTimezone(workspaceId);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Stabilize timestamp so the query key doesn't change on every render
  const now = useMemo(() => Math.floor(Date.now() / 1000), []);
  const days = 30;
  const start = now - days * 86400;

  const query = useServicesQuery(
    'getAllDeliveries',
    {
      params: { environment_id: workspaceId },
      query: { version: '2', res: 'days', start, end: now, tz },
    },
    { staleTime: 300_000, enabled: workspaceId > 0 && isReady },
  );
  return { ...query, tz };
}

const GHOST_BAR_HEIGHTS = [
  30, 50, 40, 60, 50, 70, 80, 60, 50, 70, 90, 80, 60, 70, 50, 60, 80, 100, 80, 60, 50, 70, 80, 70, 50, 60, 80, 90, 70,
  60,
];
const GHOST_CHART_HEIGHT = 220;
const GHOST_BAR_WIDTH = 18;
const GHOST_BAR_GAP = 4;
const GHOST_MAX = Math.max(...GHOST_BAR_HEIGHTS);

function GhostChart() {
  const totalWidth = GHOST_BAR_HEIGHTS.length * (GHOST_BAR_WIDTH + GHOST_BAR_GAP) - GHOST_BAR_GAP;
  return (
    <svg
      viewBox={`0 0 ${totalWidth} ${GHOST_CHART_HEIGHT}`}
      width="100%"
      height={GHOST_CHART_HEIGHT}
      preserveAspectRatio="none"
      aria-hidden
    >
      {GHOST_BAR_HEIGHTS.map((h, index) => {
        const barH = (h / GHOST_MAX) * (GHOST_CHART_HEIGHT * 0.85);
        return (
          <rect
            key={index}
            x={index * (GHOST_BAR_WIDTH + GHOST_BAR_GAP)}
            y={GHOST_CHART_HEIGHT - barH}
            width={GHOST_BAR_WIDTH}
            height={barH}
            rx={1}
            fill="currentColor"
          />
        );
      })}
    </svg>
  );
}

export function ActivityChart({ workspaceId, isStale = false }: ActivityChartProps) {
  const { track } = useAnalytics();
  const { data, isPending, tz } = useDeliveryMetrics(workspaceId);
  const [channel, setChannel] = useState<ChannelOptionValue>('all');

  const channelOptions = useMemo(() => getEnabledChannelOptions(data?.metrics ?? []), [data?.metrics]);
  const firstChannelWithData = useMemo(() => {
    const metrics = data?.metrics ?? [];
    return channelOptions.find((o) => {
      const { sent } = aggregateMetrics(metrics, tz, o.apiTypes);
      return sent.some((v) => v > 0);
    })?.value;
  }, [channelOptions, data?.metrics, tz]);
  const effectiveChannel = channelOptions.some((o) => o.value === channel) ? channel : firstChannelWithData ?? 'all';
  const selectedOption =
    CHANNEL_OPTIONS.find((o) => o.value === effectiveChannel) ?? channelOptions[0] ?? ALL_CHANNELS_OPTION;
  const showDelivered = channelSupportsDelivered(effectiveChannel);
  const showOpens = channelSupportsOpens(effectiveChannel);

  const { sent: allSent } = useMemo(() => aggregateMetrics(data?.metrics ?? [], tz, null), [data?.metrics, tz]);
  const hasData = allSent.some((v) => v > 0);

  const { sent, delivered, opened, clicked, categories } = useMemo(
    () => aggregateMetrics(data?.metrics ?? [], tz, selectedOption.apiTypes),
    [data?.metrics, tz, selectedOption.apiTypes],
  );

  const clickToOpenRate = useMemo(
    () => (showOpens ? opened.map((o, index) => (o > 0 ? Math.round((clicked[index] / o) * 1000) / 10 : 0)) : []),
    [opened, clicked, showOpens],
  );

  const chartOptions = useMemo(
    () => ({
      chart: { type: 'column' as const, height: 280, spacing: [8, 16, 8, 8] },
      xAxis: { categories, labels: { step: 5 } },
      yAxis: [
        { title: { text: undefined }, endOnTick: true },
        ...(showOpens
          ? [
              {
                title: { text: undefined },
                labels: { format: '{value}%' },
                opposite: true,
                min: 0,
                ceiling: 100,
                maxPadding: 0.1,
                gridLineWidth: 0,
                endOnTick: true,
              },
            ]
          : []),
      ],
      tooltip: { shared: true },
      legend: { align: 'left' as const, verticalAlign: 'top' as const },
      plotOptions: {
        column: { grouping: false, centerInCategory: true, groupPadding: 0.08, borderRadius: 1, borderWidth: 0 },
      },
      series: [
        { name: 'Sent', data: sent, pointPadding: 0.15, pointPlacement: 0, colorIndex: 0 },
        ...(showDelivered
          ? [{ name: 'Delivered', data: delivered, pointPadding: 0.25, pointPlacement: 0, colorIndex: 1 }]
          : []),
        ...(showOpens
          ? [
              { name: 'Opened', data: opened, pointPadding: 0.35, pointPlacement: 0, colorIndex: 2 },
              {
                name: 'Open-to-click rate',
                type: 'spline' as const,
                data: clickToOpenRate,
                yAxis: 1,
                lineWidth: 1.5,
                marker: { enabled: false },
                tooltip: { valueSuffix: '%' },
                colorIndex: 3,
              },
            ]
          : []),
      ],
    }),
    [sent, delivered, opened, clickToOpenRate, categories, showDelivered, showOpens],
  );

  return (
    <Box w="full">
      <Flex alignItems="center" justifyContent="space-between" mb="150">
        <SectionLabel>{isStale ? 'See how your messages perform' : "How's your workspace doing?"}</SectionLabel>
        <Flex alignItems="center" gap="200">
          {channelOptions.length > 0 && hasData ? (
            <Select
              ariaLabel="Channel filter"
              value={effectiveChannel}
              size="sm"
              onChange={(value) => {
                const selected =
                  (value as ChannelOptionValue) ?? firstChannelWithData ?? channelOptions[0]?.value ?? 'all';
                setChannel(selected);
                track('home', 'workspaceActivityGraphEdited', { Channel: selected });
              }}
              options={channelOptions.map((o) => ({ value: o.value, label: o.label }))}
            />
          ) : null}
          <Text size="xs">
            <Link
              href={analysisHref(workspaceId)}
              variant="primary"
              icon="chevron-right"
              iconPosition="trailing"
              onClick={() => track('home', 'clickedAnalysisLink', {})}
            >
              Analysis
            </Link>
          </Text>
        </Flex>
      </Flex>
      {isPending ? (
        <Card style={{ border: 'none' }}>
          <CardSection>
            <SkeletonShape style={{ width: '100%', height: 280 }} />
          </CardSection>
        </Card>
      ) : !hasData ? (
        <div className={styles.ghostContainer}>
          <div className={styles.ghostChart}>
            <GhostChart />
          </div>
          <div className={styles.ghostOverlay}>
            <Flex direction="column" gap="100" alignItems="center">
              <LabelIcon name="analysis" color="teal" shape="square" size="lg" />
              <Text size="m" weight="semibold" textAlign="center">
                Start sending to see your activity
              </Text>
              <Text size="s" color="subtle" textAlign="center">
                Sends and deliveries from the last 30 days will appear here.
              </Text>
              <Flex gap="100" pt="100">
                <Button
                  variant="tertiary"
                  size="sm"
                  href={generatePath('/workspaces/:workspaceId/journeys/campaigns/new', { workspaceId })}
                >
                  New campaign
                </Button>
                <Button
                  variant="tertiary"
                  size="sm"
                  href={generatePath('/workspaces/:workspaceId/journeys/broadcasts/new', { workspaceId })}
                >
                  New broadcast
                </Button>
                <Button
                  variant="tertiary"
                  size="sm"
                  href={generatePath('/workspaces/:workspaceId/journeys/transactional/new', { workspaceId })}
                >
                  New transactional
                </Button>
              </Flex>
            </Flex>
          </div>
        </div>
      ) : (
        <>
          <Chart options={chartOptions} />
          <Text size="xs" color="subtle" mt="100" ml="100">
            Metrics vary by channel.{' '}
            <Link href="https://docs.customer.io/journeys/analytics/" variant="primary" isExternal>
              Learn more.
            </Link>
          </Text>
        </>
      )}
    </Box>
  );
}
